import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { BackendApi } from "../api/api.ts";
import { User } from "../api/accessControl/User.ts";
import { BackendAPITypes } from "../generated/blueprint.ts";
import { ALL_LEGAL_ENTITY_TYPES } from "../types/enums.ts";
import type {DocumentDirection, LegalEntityType} from "../generated/enums.ts";

export const DEFAULT_GLA_ID = "BSchTusTovTvp";
export const VAT_GLA_ID = "BSchBepBtw";
export const BANK_ACCOUNT_BALANCE_GLA_ID = "BLimBan";

export interface GlAccount {
    id: string;
    rgs_number: string;
    rgs_official_label: string;
    ranking: number;
    label: string;
    label_nl: string;
    legacy_bankcat: string;
    legacy_bankcat_nl: string;
    cashflow_subcategory: string;
    cashflow_subcategory_nl: string;
    cashflow_category: string | null;
    cashflow_category_nl: string | null;
    icon: string | null;
    applicable_bill_direction?: DocumentDirection;
    applicable_legal_entity_type?: LegalEntityType[];
}

type GeneralLedgerAccountApiItem =
    BackendAPITypes.BACKENDPreHandleSyncApiV1GeneralLedgerAccountsGetResponseBody["items"][number];

export const DEFAULT_GL_ACCOUNT: GlAccount = {
    id: DEFAULT_GLA_ID,
    rgs_number: "1229020",
    rgs_official_label: "Tussenrekening vraagposten",
    ranking: 63,
    label: "Suspense account",
    label_nl: "Tussenrekening vraagposten",
    legacy_bankcat: "queue_yet_to_be_classified",
    legacy_bankcat_nl: "Wachtrij — nog te classificeren",
    cashflow_subcategory: "queue_yet_to_be_classified",
    cashflow_subcategory_nl: "Wachtrij — nog te classificeren",
    cashflow_category: null,
    cashflow_category_nl: null,
    icon: "ri-time-line",
    applicable_legal_entity_type: [...ALL_LEGAL_ENTITY_TYPES],
};

export const BANK_GL_ACCOUNT: GlAccount = {
    id: BANK_ACCOUNT_BALANCE_GLA_ID,
    rgs_number: "1002000",
    rgs_official_label: "Tegoeden bij banken",
    ranking: 28,
    label: "Bank",
    label_nl: "Bank",
    legacy_bankcat: "bank_account_balance",
    legacy_bankcat_nl: "Saldo bankrekening",
    cashflow_subcategory: "bank_account_balance",
    cashflow_subcategory_nl: "Saldo bankrekening",
    cashflow_category: null,
    cashflow_category_nl: null,
    icon: "ri-bank-line",
    applicable_legal_entity_type: [...ALL_LEGAL_ENTITY_TYPES],
};

function mapApiItemToGlAccount(item: GeneralLedgerAccountApiItem): GlAccount {
    return {
        ...item,
        applicable_bill_direction: (item.applicable_bill_direction as DocumentDirection | null) ?? undefined,
        applicable_legal_entity_type:
            (item.applicable_legal_entity_type as LegalEntityType[] | null) ?? undefined,
    };
}

export const useGLAccounts = (user: User) => {
    const [glAccounts, setGlAccounts] = useState<GlAccount[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const token = user.getToken();

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        const api = new BackendApi(token);

        async function fetchCategories() {
            try {
                const response: BackendAPITypes.BACKENDPreHandleSyncApiV1GeneralLedgerAccountsGetResponseBody =
                    await api.GeneralLedgerAccounts({});
                if (cancelled) return;
                const list = response?.items && Array.isArray(response.items) ? response.items : [];

                const mappedAccounts: GlAccount[] = list.map(mapApiItemToGlAccount);

                if (!mappedAccounts.some((account) => account.id === DEFAULT_GLA_ID)) {
                    mappedAccounts.push(DEFAULT_GL_ACCOUNT);
                }

                setGlAccounts(mappedAccounts);
            } catch (error) {
                Sentry.captureException(error);
                if (!cancelled) {
                    setGlAccounts([DEFAULT_GL_ACCOUNT]);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        fetchCategories();
        return () => {
            cancelled = true;
        };
    }, [token]);

    return { glAccounts, isLoading };
};
